import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Courses.css";
import { FaClock, FaUserGraduate, FaShoppingCart } from "react-icons/fa";
import { getCourseBatch, API_BASE } from "../Services/Api";
import { useCart } from "../Context/Cartcontext";
import { useAuth } from "../Context/AuthContext";

const Courses = ({ categoryId, scrollRef }) => {
  const { cartItems, addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [popup, setPopup] = useState({ show: false, courseName: "" });
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    const fetchBatch = async () => {
      try {
        setLoading(true);
        setApiError("");

        const res = await getCourseBatch(categoryId);

        console.log("COURSE BATCH RESPONSE:", res);
        console.log("ENROLLED COURSES:", res?.enrolled_courses);

        setCourses(Array.isArray(res?.courses) ? res.courses : []);
        setCategories(Array.isArray(res?.categories) ? res.categories : []);
        setEnrolledCourses(
          Array.isArray(res?.enrolled_courses) ? res.enrolled_courses : []
        );
      } catch (error) {
        console.error("Batch API error:", error);
        setApiError(error?.message || "Unable to load courses.");
        setCourses([]);
        setCategories([]);
        setEnrolledCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [categoryId]);

  const enrolledCourseIds = useMemo(() => {
    return new Set(
      enrolledCourses
        .map((enrolled) =>
          parseInt(enrolled.all_course_id ?? enrolled.main_course, 10)
        )
        .filter((id) => !Number.isNaN(id))
    );
  }, [enrolledCourses]);

  const displayCourses = useMemo(() => {
    const merged = [...courses];

    enrolledCourses.forEach((enrolled) => {
      const enrolledId = parseInt(
        enrolled.all_course_id ?? enrolled.main_course,
        10
      );

      if (Number.isNaN(enrolledId)) return;

      const alreadyInCourses = merged.some(
        (course) => parseInt(course.id, 10) === enrolledId
      );

      if (!alreadyInCourses) {
        merged.push({
          id: enrolledId,
          category_id: enrolled.category_id ?? null,
          category_name: enrolled.category_name ?? "Enrolled Course",
          course_name: enrolled.course_name ?? "Course",
          course_hrs: enrolled.course_hrs ?? "",
          course_price: enrolled.course_price ?? 0,
          gst_precent: enrolled.gst_precent ?? 0,
          gst_flag: enrolled.gst_flag ?? 1,
          image: enrolled.image ?? null,
          card_design: 1,
          _fromEnrollment: true,
        });
      }
    });

    if (categoryId) {
      return merged.filter((course) => {
        if (!course.category_id) return true;

        return (
          parseInt(course.category_id, 10) === parseInt(categoryId, 10)
        );
      });
    }

    return merged.filter((course) => {
      const id = parseInt(course.id, 10);

      return (
        parseInt(course.card_design, 10) === 1 ||
        enrolledCourseIds.has(id)
      );
    });
  }, [courses, enrolledCourses, enrolledCourseIds, categoryId]);

  const handleAddToCart = (course) => {
    if (!user) {
      navigate("/login", {
        state: { from: `/about-course/${course.id}` },
      });
      return;
    }

    const alreadyAdded = cartItems.some(
      (item) => parseInt(item.id, 10) === parseInt(course.id, 10)
    );

    if (alreadyAdded) {
      alert("Course is already in your cart!");
      return;
    }

    addToCart(course);

    setPopup({ show: true, courseName: course.course_name });

    setTimeout(() => {
      setPopup({ show: false, courseName: "" });
    }, 3000);
  };

  const handleGetAccess = async (moodleCourseId) => {
    try {
      const token = sessionStorage.getItem("access_token");

      const res = await fetch(`${API_BASE}/connect-course`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/html,application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ courseId: moodleCourseId }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Connect course failed:", res.status, errorText);
        return;
      }

      const html = await res.text();
      document.open();
      document.write(html);
      document.close();
    } catch (err) {
      console.error("Error connecting to course:", err);
    }
  };

  return (
    <>
      <title>EDC - Courses</title>

      <section className="popular-courses" ref={scrollRef}>
        <div className="container">
          <div className="section-title">
            <h2 className="text-4xl font-extrabold text-gray-900 leading-tight">
              {categoryId ? "Courses in Selected Category" : "Popular Courses"}
            </h2>
            <p>Trending courses picked by our community this week</p>
          </div>

          {loading ? (
            <div className="course-grid">
              {[...Array(3)].map((_, idx) => (
                <div className="course-card" key={idx}>
                  <div className="course-image skeleton skeleton-image"></div>
                  <div className="course-content">
                    <div className="skeleton skeleton-title"></div>
                    <div className="skeleton skeleton-text"></div>
                    <div className="skeleton skeleton-text"></div>
                    <div className="skeleton skeleton-button"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : apiError ? (
            <p className="text-center text-red-500">{apiError}</p>
          ) : displayCourses.length === 0 ? (
            <p className="text-center text-gray-500">No courses found.</p>
          ) : (
            <div className="course-grid">
              {displayCourses.map((course) => {
                const courseId = parseInt(course.id, 10);

                const enrolledObj = enrolledCourses.find((enrolled) => {
                  const enrolledId = parseInt(
                    enrolled.all_course_id ?? enrolled.main_course,
                    10
                  );

                  return enrolledId === courseId;
                });

                const isEnrolled = !!enrolledObj;

                const inCart = cartItems.some(
                  (item) => parseInt(item.id, 10) === courseId
                );

                const gstPercent = parseFloat(course.gst_precent) || 0;
                const basePrice = parseFloat(course.course_price) || 0;

                const totalPrice =
                  parseInt(course.gst_flag, 10) === 0
                    ? basePrice + (basePrice * gstPercent) / 100
                    : basePrice;

                return (
                  <div
                    className="course-card"
                    key={courseId}
                    onClick={() => navigate(`/about-course/${course.id}`)}
                  >
                    <div className="course-image">
                      <img
                        src={
                          course.image
                            ? `https://edcbackend.lsc-india.org/${course.image}`
                            : "/default-course.jpg"
                        }
                        alt={course.course_name}
                        style={{
                          width: "100%",
                          height: "auto",
                          cursor: "pointer",
                        }}
                      />
                    </div>

                    <div className="course-content">
                      <span className="course-category">
                        {course.category_name}
                      </span>

                      <h3 className="course-title">{course.course_name}</h3>

                      <div className="course-meta mt-5">
                        <span className="meta-item">
                          <FaClock className="icon" />
                          {course.course_hrs || "—"}
                        </span>

                        <span className="meta-item">
                          <FaUserGraduate className="icon" />₹
                          {Math.round(totalPrice)}
                          {parseInt(course.gst_flag, 10) === 0 && (
                            <small>
                              {" "}
                              (with {gstPercent}% GST)
                            </small>
                          )}
                        </span>
                      </div>

                      <div className="course-footer">
                        <Link
                          to={`/about-course/${course.id}`}
                          className="view-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Course
                        </Link>

                        {isEnrolled ? (
                          <button
                            className="add-to-cart-icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGetAccess(enrolledObj.moodle_course_id);
                            }}
                          >
                            Get Access
                          </button>
                        ) : inCart ? (
                          <button
                            className="add-to-cart-icon-btn"
                            disabled
                            onClick={(e) => e.stopPropagation()}
                          >
                            Added in Cart
                          </button>
                        ) : (
                          <button
                            className="add-to-cart-icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(course);
                            }}
                          >
                            <FaShoppingCart className="mx-2" />
                            Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {popup.show && (
          <div className="cart-popup">
            <p>
              <strong>{popup.courseName}</strong> has been added to your cart!
            </p>
          </div>
        )}
      </section>
    </>
  );
};

export default Courses;